<template>
    <v-expansion-panels v-model="panel" class="my-4">
        <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-bold">
                Folios otorgados por beneficio
            </v-expansion-panel-header>

            <v-expansion-panel-content>
                <div class="d-flex align-center mb-4">
                    <v-btn icon :disabled="loading" @click="cambiarMes(-1)">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <span class="text-subtitle-1 font-weight-bold text-capitalize mx-2 titulo-mes">{{ titulo }}</span>
                    <v-btn icon :disabled="loading || isMesActual" @click="cambiarMes(1)">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                    <v-spacer />
                    <v-progress-circular v-if="loading" indeterminate size="20" color="#b11f1f" />
                </div>

                <v-row dense class="mb-4">
                    <v-col cols="6">
                        <v-sheet color="grey lighten-4" rounded class="pa-3">
                            <div class="caption grey--text text--darken-1">Total del mes</div>
                            <div class="text-h5">{{ fmt(totalActual) }}</div>
                        </v-sheet>
                    </v-col>
                    <v-col cols="6">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-sheet v-bind="attrs" v-on="on" color="grey lighten-4" rounded class="pa-3">
                                    <div class="caption grey--text text--darken-1">Vs mes anterior</div>
                                    <div class="text-h5" :class="colorVariacion(variacionTotal)">
                                        {{ formatVariacion(variacionTotal) }}
                                    </div>
                                </v-sheet>
                            </template>
                            <div>{{ tituloAnterior }}: {{ fmt(totalAnterior) }}</div>
                            <div>{{ tituloActual }}: {{ fmt(totalActual) }}</div>
                            <div>Diferencia: {{ diferencia(totalActual, totalAnterior) }}</div>
                        </v-tooltip>
                    </v-col>
                </v-row>

                <div v-if="!filas.length && !loading" class="text-center grey--text py-4">
                    Sin folios otorgados en este mes
                </div>

                <div v-for="f in filas" :key="f.beneficio" class="fila-beneficio">
                    <span class="nombre" :title="f.beneficio">{{ f.beneficio }}</span>
                    <div class="d-flex align-center">
                        <div class="barra-contenedor">
                            <div class="barra" :style="{ width: anchoBarra(f.actual) }"></div>
                        </div>
                        <span class="ml-2 text-no-wrap">
                            {{ fmt(f.actual) }} <span class="grey--text">· {{ porcentaje(f.actual) }}%</span>
                        </span>
                    </div>
                    <div class="text-right">
                        <v-tooltip left>
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on" class="text-no-wrap" :class="colorVariacion(f.variacion)">
                                    {{ formatVariacion(f.variacion) }}
                                </span>
                            </template>
                            <template v-if="f.anterior">
                                <div>{{ tituloAnterior }}: {{ fmt(f.anterior) }}</div>
                                <div>{{ tituloActual }}: {{ fmt(f.actual) }}</div>
                                <div>Diferencia: {{ diferencia(f.actual, f.anterior) }}</div>
                            </template>
                            <div v-else>Sin folios en {{ tituloAnterior.toLowerCase() }}, no hay base de comparación</div>
                        </v-tooltip>
                    </div>
                </div>
            </v-expansion-panel-content>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script>
import { getResumenFoliosPorBeneficio  } from "@/services/redencionService.js";

const pad = (n) => String(n).padStart(2, "0");

export default {
    data() {
        const hoy = new Date();
        return {
            panel: 1,
            anio: hoy.getFullYear(),
            mes: hoy.getMonth() + 1,
            datos: [],
            loading: false,
            requestId: 0,
        };
    },
    computed: {
        keyActual() {
            return `${this.anio}-${pad(this.mes)}`;
        },
        keyAnterior() {
            const d = new Date(this.anio, this.mes - 2, 1);
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
        },
        titulo() {
            return new Date(this.anio, this.mes - 1, 1).toLocaleDateString("es-MX", {
                month: "long",
                year: "numeric",
            });
        },
        isMesActual() {
            const hoy = new Date();
            return this.anio === hoy.getFullYear() && this.mes === hoy.getMonth() + 1;
        },
        filas() {
            const map = {};
            this.datos.forEach(({ beneficio, periodo, total }) => {
                if (!map[beneficio]) map[beneficio] = { beneficio, actual: 0, anterior: 0 };
                if (periodo === this.keyActual) map[beneficio].actual = Number(total);
                if (periodo === this.keyAnterior) map[beneficio].anterior = Number(total);
            });
            return Object.values(map)
                .filter((f) => f.actual > 0 || f.anterior > 0)
                .map((f) => ({ ...f, variacion: this.calcVariacion(f.actual, f.anterior) }))
                .sort((a, b) => b.actual - a.actual);
        },
        totalActual() {
            return this.filas.reduce((acc, f) => acc + f.actual, 0);
        },
        totalAnterior() {
            return this.filas.reduce((acc, f) => acc + f.anterior, 0);
        },
        variacionTotal() {
            return this.calcVariacion(this.totalActual, this.totalAnterior);
        },
        maxActual() {
            return Math.max(0, ...this.filas.map((f) => f.actual));
        },
        tituloActual() {
            return this.nombreMes(this.anio, this.mes);
        },
        tituloAnterior() {
            const d = new Date(this.anio, this.mes - 2, 1);
            return this.nombreMes(d.getFullYear(), d.getMonth() + 1);
        },
    },
    watch: {
        keyActual: {
            immediate: true,
            handler() {
                this.loadResumen();
            },
        },
    },
    methods: {
        async loadResumen() {
            const id = ++this.requestId;
            this.loading = true;
            try {
                const response = await getResumenFoliosPorBeneficio (this.anio, this.mes);
                if (id !== this.requestId) return;
                this.datos = response || [];
            } catch (error) {
                if (id === this.requestId) this.$toast.error(error.message);
            } finally {
                if (id === this.requestId) this.loading = false;
            }
        },
        cambiarMes(delta) {
            const d = new Date(this.anio, this.mes - 1 + delta, 1);
            this.anio = d.getFullYear();
            this.mes = d.getMonth() + 1;
        },
        calcVariacion(actual, anterior) {
            if (!anterior) return null;
            return ((actual - anterior) / anterior) * 100;
        },
        formatVariacion(v) {
            if (v === null) return "—";
            const r = Math.round(v * 10) / 10;
            if (r === 0) return "0%";
            return `${r > 0 ? "▲ +" : "▼ "}${r.toFixed(1)}%`;
        },
        colorVariacion(v) {
            if (v === null || Math.round(v * 10) === 0) return "";
            return v > 0 ? "green--text" : "red--text";
        },
        anchoBarra(valor) {
            if (!this.maxActual || !valor) return "0%";
            return `${Math.max((valor / this.maxActual) * 100, 1)}%`;
        },
        porcentaje(valor) {
            if (!this.totalActual) return "0.0";
            return ((valor / this.totalActual) * 100).toFixed(1);
        },
        fmt(n) {
            return Number(n || 0).toLocaleString("es-MX");
        },
        nombreMes(anio, mes) {
            const texto = new Date(anio, mes - 1, 1).toLocaleDateString("es-MX", {
                month: "long",
                year: "numeric",
            });
            return texto.charAt(0).toUpperCase() + texto.slice(1);
        },
        diferencia(actual, anterior) {
            const d = actual - anterior;
            return `${d > 0 ? "+" : ""}${this.fmt(d)} folios`;
        },
    },
};
</script>

<style scoped>
.titulo-mes {
    min-width: 140px;
    text-align: center;
}

.fila-beneficio {
    display: grid;
    grid-template-columns: 200px minmax(0, 1fr) 100px;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    font-size: 13px;
}

.nombre {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.barra-contenedor {
    flex: 1;
    min-width: 0;
}

.barra {
    height: 18px;
    background: #b11f1f;
    border-radius: 4px;
}
</style>